import type { ComponentChildren } from 'preact';
import { create } from 'zustand';

/**
 * Transient UI state: the modal stack and the toast.
 *
 * The original app kept one `#modal` div and filled it with an HTML string.
 * Here the modal holds JSX children instead, so dialog content is ordinary
 * typed markup with real event handlers rather than `onclick="..."` strings
 * pointing at globals.
 *
 * A stack rather than a single slot, because a dialog sometimes needs to ask
 * for something before it can be filled in — picking attendus from the
 * référentiel is a long, scrollable list that has no business being squeezed
 * inside the lesson form. Stacking keeps the form mounted underneath, so
 * everything already typed into it survives.
 */

interface ModalState {
  title: string;
  body: ComponentChildren;
}

interface UiState {
  modals: ModalState[];
  toast: string | null;
  /** Open a dialog, replacing anything already open. */
  openModal: (title: string, body: ComponentChildren) => void;
  /** Open a dialog on top of the current one, which stays mounted. */
  pushModal: (title: string, body: ComponentChildren) => void;
  /** Close the topmost dialog. */
  closeModal: () => void;
  /** Close every dialog, whatever the depth. */
  closeAllModals: () => void;
  showToast: (message: string) => void;
  clearToast: () => void;
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;

export const useUi = create<UiState>()((set) => ({
  modals: [],
  toast: null,

  openModal: (title, body) => set({ modals: [{ title, body }] }),
  pushModal: (title, body) => set((s) => ({ modals: [...s.modals, { title, body }] })),
  closeModal: () => set((s) => ({ modals: s.modals.slice(0, -1) })),
  closeAllModals: () => set({ modals: [] }),

  showToast: (message) => {
    clearTimeout(toastTimer);
    set({ toast: message });
    toastTimer = setTimeout(() => set({ toast: null }), 1700);
  },

  clearToast: () => {
    clearTimeout(toastTimer);
    set({ toast: null });
  },
}));

/** Imperative helpers, so callers outside components can reach the UI. */
export const openModal = (title: string, body: ComponentChildren): void =>
  useUi.getState().openModal(title, body);

export const pushModal = (title: string, body: ComponentChildren): void =>
  useUi.getState().pushModal(title, body);

export const closeModal = (): void => useUi.getState().closeModal();

export const closeAllModals = (): void => useUi.getState().closeAllModals();

export const toast = (message: string): void => useUi.getState().showToast(message);
